// Drives the Google Places provider search on the SCA case form
// (partners/provider_access_cases/new). Google Places is the form's only
// provider-search surface.
//
// Flow:
//   1. Debounced search  -> POST (body, never query string) -> results turbo stream
//   2. Click a place     -> the other results collapse away, POST place_id ->
//      server resolves candidates -> turbo stream. Clicking it again deselects
//      and brings the other results back.
//   3. Click a matched candidate card -> its signed token is copied into the
//      hidden candidate_token field and the form submits as soon as a member
//      is also present (no page-level CTA). Click the "Other" card -> it
//      expands NPI/EIN inputs plus its own Continue button, which stays
//      disabled until a member and both identifiers are present. Clicking a
//      selected card again deselects it.
//
// The server enforces every guard (min length, capped results, resolution,
// token signing/verification); this controller is purely presentational.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "candidates", "submit", "manualFields", "token"];
  static values = {
    searchUrl: String,
    selectUrl: String,
    minLength: { type: Number, default: 3 },
    wait: { type: Number, default: 300 },
    // Mirrors _provider_result_card's `selected:` styling for a client-side
    // pick; toggled on the clicked card wrapper.
    selectedClasses: { type: String, default: "border-core-purple-500 bg-[#FCFCFF] shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]" },
    unselectedClasses: { type: String, default: "border-support-light-300" },
    // Toggled on the search input while a Google search is in flight (the same
    // shimmer the Provider Intelligence Add Provider drawer uses).
    busyClass: { type: String, default: "" },
  };

  connect() {
    this.timeout = null;
    this.searchController = null;
    this.selectController = null;
    this.googleActive = false;
    this.submitted = false;
    // Auto-submit fires only off an explicit gesture (a matched-card click or a
    // member pick), never from restoring state on connect: a preserved matched
    // candidate re-rendered by a 422 would otherwise resubmit itself in a loop.
    this.autoSubmitArmed = false;
    this.onSubmitEnd = this.handleSubmitEnd.bind(this);
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd);
    // Bumped every time the query changes or a local search/selection takes
    // ownership. A response captures the generation it was dispatched under and
    // refuses to render if it is now stale — a belt-and-suspenders guard against
    // an out-of-order completion slipping past the AbortController.
    this.generation = 0;
    this.adoptPreselectedCandidate();
  }

  // A validation re-render (e.g. a missing member) arrives with the verified
  // Google selection already chosen: the hidden token plus, for a manual-entry
  // candidate, whatever NPI/EIN the operator had already typed. Adopt it so
  // Continue reflects it and a new search still clears it correctly.
  adoptPreselectedCandidate() {
    const preserved = this.element.querySelector("[data-testid='sca-google-places-preserved']");
    if (preserved?.querySelector("input[type='hidden'][name='candidate_token']")) {
      this.googleActive = true;
    }
    this.updateSubmit();
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    if (this.timeout) clearTimeout(this.timeout);
    if (this.searchController) this.searchController.abort();
    if (this.selectController) this.selectController.abort();
  }

  // ---- Search -----------------------------------------------------------

  onSearchInput() {
    // Starting a Google search transfers ownership away from any previously
    // selected Goodbill record immediately. This clears its hidden scope/id and
    // disables submit during the debounce, so Enter cannot submit a stale local
    // provider while the operator is visibly switching search paths.
    this.dispatch("selected");
    // Typing a new query voids any prior Google selection AND invalidates any
    // in-flight place-details request so a late candidates response can't
    // repopulate the region under a query the operator has already changed.
    this.generation += 1;
    this.resetGoogleSelection();
    // Remove old candidate cards immediately, not after the debounce: otherwise
    // an operator can click and submit a candidate from the previous query.
    this.clearCandidates();
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.runSearch(), this.waitValue);
  }

  runSearch() {
    const query = this.inputTarget.value;
    const trimmed = query.trim();
    // Below the floor (including an emptied box) we make no request and clear any
    // stale results, candidates, and Google selection so old matches can neither
    // linger on screen nor be submitted. The server enforces the same minimum.
    if (trimmed.length < this.minLengthValue) {
      this.abortSearch();
      this.abortSelect();
      // The aborted search's finally() sees a newer generation and leaves the
      // shimmer alone, so the owner of this generation must clear it here.
      this.setBusy(false);
      this.clearResults();
      this.clearCandidates();
      this.resetGoogleSelection();
      return;
    }

    // A new valid search supersedes both any prior search AND any in-flight
    // place-details request — the candidates it would render belong to a place
    // the operator is no longer looking at.
    this.abortSearch();
    this.abortSelect();
    this.searchController = new AbortController();
    const generation = this.generation;
    this.clearCandidates();
    // No loading row: the in-flight state is the shimmer on the input itself.
    this.setBusy(true);

    this.postTurboStream(
      this.searchUrlValue,
      { place_query: query, member_id: this.memberId() },
      this.searchController.signal,
      // A hard fetch/transport failure renders the same "unavailable" state the
      // server sends on the happy path, never a hung loading row or a dead box.
      () => this.showError(this.resultsTarget, "Provider search is temporarily unavailable. Please try again.", "sca-google-places-error"),
      () => generation === this.generation,
    ).finally(() => {
      // A superseded search leaves the shimmer to the request that now owns it.
      if (generation === this.generation) this.setBusy(false);
    });
  }

  // ---- Select a place ---------------------------------------------------

  selectPlace(event) {
    const card = event.currentTarget;
    const placeId = card.dataset.placeId;
    if (!placeId) return;

    // Clicking the selected place again deselects it: the other results come
    // back and the billing-identity region (and any in-flight lookup) is dropped.
    if (this.isSelectedCard(card)) {
      this.generation += 1;
      this.abortSelect();
      this.markSelectedCard(null, this.placeCards());
      this.clearCandidates();
      this.resetGoogleSelection();
      return;
    }

    // The chosen place card stays on screen in its selected state and the
    // other results collapse; only the billing-identity region below reloads.
    this.markSelectedCard(card, this.placeCards());
    this.resetGoogleSelection();
    this.abortSelect();
    this.selectController = new AbortController();
    const generation = this.generation;
    this.showLoading(this.candidatesTarget, "Matching a billing NPI and EIN…");

    this.postTurboStream(
      this.selectUrlValue,
      { place_id: placeId, member_id: this.memberId() },
      this.selectController.signal,
      // Same guarantee as search: a transport failure surfaces the unavailable
      // state in the candidates region instead of leaving the loader spinning.
      () => this.showError(this.candidatesTarget, "Provider lookup is temporarily unavailable. Please try again.", "sca-google-places-candidates-error"),
      // Never repopulate candidates for a place the operator has moved on from
      // (query changed, or a local search/selection took ownership mid-flight).
      () => generation === this.generation,
    );
  }

  // ---- Choose a candidate ------------------------------------------------

  // Selects a billing identity: the card's signed token becomes the form's
  // candidate_token (enabled so Turbo includes it on submit), the card is
  // highlighted and its siblings collapse. A matched card submits right away
  // (once a member is present); the "Other" card expands its NPI/EIN inputs
  // and waits for its own Continue. Clicking a selected card again deselects.
  chooseCandidate(event) {
    const card = event.currentTarget;
    const { token, manual } = card.dataset;
    if (!token || !this.hasTokenTarget) return;

    if (this.isSelectedCard(card)) {
      this.resetGoogleSelection();
      return;
    }

    this.googleActive = true;
    // Hand off to the local (access-issue-form) controller so it clears its own
    // hidden scope/id AND its visible selected card/details — the two paths must
    // never both look or submit as selected. Fires before we (re-)enable submit
    // because the local controller's clear disables it as a side effect.
    this.dispatch("selected");
    this.tokenTarget.value = token;
    this.tokenTarget.disabled = false;
    this.markSelectedCard(card, this.candidateCards());
    if (manual === "true") {
      this.autoSubmitArmed = false;
      this.showManualEntry();
      this.updateSubmit();
    } else {
      this.autoSubmitArmed = true;
      this.hideManualEntry();
      this.updateSubmit();
    }
  }

  // Member picker change: a matched card chosen before the member (or restored
  // by a validation re-render) completes now that the operator acted again.
  memberChanged() {
    this.autoSubmitArmed = true;
    this.updateSubmit();
  }

  // A failed or cancelled submission (422, network error) must leave the form
  // retryable: the next explicit gesture may submit again.
  handleSubmitEnd(event) {
    if (event.detail?.success) return;
    this.submitted = false;
    this.autoSubmitArmed = false;
  }

  chooseManualCandidate() {
    this.googleActive = true;
    this.dispatch("selected");
    this.updateSubmit();
  }

  // Clicks inside the Other card's inputs must not toggle the card itself.
  stopPropagation(event) {
    event.stopPropagation();
  }

  // The Other card's Continue is enabled only when every required field is
  // present: a member plus both manual identifiers. Wired to the form's
  // bubbling input / autocomplete.change events so the member picker and the
  // NPI/EIN fields both refresh it; a member picked after a matched card was
  // chosen completes that pick by submitting.
  updateSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !(this.memberId() && this.billingIdentityPresent());
    }
    this.submitIfReady();
  }

  // Matched (non-manual) picks have no CTA: the form submits itself the moment
  // both a member and the signed token are present, provided the trigger was an
  // explicit gesture (see autoSubmitArmed). Manual entry always waits for the
  // Other card's Continue.
  submitIfReady() {
    if (this.submitted || !this.autoSubmitArmed || !this.googleActive || this.manualEntryActive()) return;
    if (!this.memberId() || !this.billingIdentityPresent()) return;
    this.submitted = true;
    this.element.requestSubmit();
  }

  manualEntryActive() {
    return this.hasManualFieldsTarget && !this.manualFieldsTarget.classList.contains("hidden");
  }

  billingIdentityPresent() {
    if (!this.hasTokenTarget || this.tokenTarget.disabled || !this.tokenTarget.value) return false;
    if (!this.manualEntryActive()) return true;
    const npi = this.manualFieldsTarget.querySelector("[name='manual_provider_npi']")?.value ?? "";
    const ein = this.manualFieldsTarget.querySelector("[name='manual_provider_ein']")?.value ?? "";
    return npi.replace(/\D/g, "").length === 10 && ein.replace(/\D/g, "").length === 9;
  }

  // Called from the local (Goodbill records) search box so the two selection
  // paths never both submit. Only disables the button if Google owned it. Also
  // takes ownership away from any in-flight Google search/select so a late
  // response can't repopulate results or candidates once the operator has
  // switched to the local box.
  onLocalSearchInput() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
  }

  // Invoked when the local (access-issue-form) controller commits a selection
  // (dispatches access-issue-form:selected). A committed local pick fully
  // supersedes Google: drop the token/banner and wipe the results + candidate
  // regions so nothing Google-side lingers on screen or in the submission.
  clearForLocalSelection() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
    this.clearResults();
    this.clearCandidates();
  }

  // ---- Helpers ----------------------------------------------------------

  async postTurboStream(url, params, signal, onFailure, shouldRender = null) {
    const body = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") body.append(key, value);
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: body.toString(),
        signal,
      });
      // A superseded-but-unaborted response (e.g. it resolved in the same tick
      // the query changed) is dropped — on both the happy path AND the failure
      // path — rather than rendering stale content or clobbering the newer
      // request's loader/results with an error it no longer owns.
      if (shouldRender && !shouldRender()) return;
      if (response.ok && !response.redirected && this.isTurboStreamResponse(response)) {
        const html = await response.text();
        // The query can change while a large/slow body is being consumed after
        // headers arrived. Recheck ownership at the final render boundary.
        if (shouldRender && !shouldRender()) return;
        window.Turbo.renderStreamMessage(html);
      } else if (onFailure) {
        // Anything that is not a genuine turbo stream — a non-2xx (5xx), a followed
        // auth redirect to an HTML login page (response.redirected, still ok), or a
        // wrong Content-Type — must NOT be handed to Turbo (it would render nothing
        // and leave the loader hung). Surface the unavailable state instead.
        onFailure();
      }
    } catch (error) {
      // An aborted request is a superseded search/select, not a failure: a newer
      // request already owns the region, so leave its loader/results untouched.
      // A late hard failure from a now-stale request is dropped for the same
      // reason it would be on the success path.
      if (error.name === "AbortError" || !onFailure) return;
      if (shouldRender && !shouldRender()) return;
      onFailure();
    }
  }

  // A genuine turbo-stream answer declares the turbo-stream Content-Type. A
  // redirected login page or any other HTML body reports a different type (or
  // none), so this guards renderStreamMessage from being fed non-stream markup.
  isTurboStreamResponse(response) {
    const contentType = response.headers?.get?.("Content-Type") || "";
    return contentType.includes("turbo-stream");
  }

  // Abort any in-flight Google search request and forget its controller so a
  // late response leaves the results region alone.
  abortSearch() {
    if (this.searchController) {
      this.searchController.abort();
      this.searchController = null;
    }
  }

  // Abort any in-flight place-details (select) request so a late response can't
  // repopulate the candidates region or its signed token after the operator has
  // moved on.
  abortSelect() {
    if (this.selectController) {
      this.selectController.abort();
      this.selectController = null;
    }
  }

  resetGoogleSelection() {
    this.clearToken();
    this.markSelectedCard(null, this.candidateCards());
    this.hideManualEntry();
    this.googleActive = false;
    this.updateSubmit();
  }

  clearToken() {
    if (!this.hasTokenTarget) return;
    this.tokenTarget.value = "";
    this.tokenTarget.disabled = true;
  }

  clearCandidates() {
    if (this.hasCandidatesTarget) this.candidatesTarget.innerHTML = "";
  }

  clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = "";
  }

  setBusy(isBusy) {
    if (!this.hasInputTarget) return;
    if (this.busyClassValue) this.inputTarget.classList.toggle(this.busyClassValue, isBusy);
    this.inputTarget.setAttribute("aria-busy", isBusy ? "true" : "false");
    if (this.hasResultsTarget) this.resultsTarget.setAttribute("aria-busy", isBusy ? "true" : "false");
  }

  // A minimal, term-free pending line (spinner + message, no card) so a slow
  // place-details lookup reads as in-progress rather than a dead click.
  showLoading(target, message) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "flex items-center gap-2 text-sm text-support-light-700";
    p.setAttribute("role", "status");
    p.setAttribute("aria-live", "polite");
    p.setAttribute("data-testid", "sca-google-places-loading");
    const spinner = document.createElement("span");
    spinner.className = "inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-core-purple-600 border-t-transparent";
    spinner.setAttribute("aria-hidden", "true");
    p.append(spinner, document.createTextNode(message));
    target.replaceChildren(p);
  }

  // Client-rendered mirror of the server's :error partials for transport
  // failures the server never got to answer. textContent keeps the (already
  // term-free) message from being interpreted as markup.
  showError(target, message, testid) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "text-sm text-alert-red-600";
    p.setAttribute("role", "alert");
    p.setAttribute("data-testid", testid);
    p.textContent = message;
    target.replaceChildren(p);
  }

  candidateCards() {
    return Array.from(this.element.querySelectorAll("[data-token]"));
  }

  placeCards() {
    return Array.from(this.element.querySelectorAll("[data-sca-place-card]"));
  }

  isSelectedCard(card) {
    return card.getAttribute("aria-selected") === "true";
  }

  // Result cards carry a checkbox in the control slot but the pick is single:
  // the active card gets the selected styling + checked box and every other
  // card in the group collapses (hidden on its list-item wrapper) until the
  // pick is cleared with activeCard = null.
  markSelectedCard(activeCard, cards) {
    const selected = this.selectedClassesValue.split(" ").filter(Boolean);
    const unselected = this.unselectedClassesValue.split(" ").filter(Boolean);
    cards.forEach((card) => {
      const isActive = card === activeCard;
      card.setAttribute("aria-selected", isActive ? "true" : "false");
      card.classList.remove(...(isActive ? unselected : selected));
      card.classList.add(...(isActive ? selected : unselected));
      const box = card.querySelector("input[type='checkbox']");
      if (box) box.checked = isActive;
      const row = card.closest("[role='listitem']") || card;
      row.classList.toggle("hidden", Boolean(activeCard) && !isActive);
    });
  }

  hideManualEntry() {
    if (!this.hasManualFieldsTarget) return;

    this.manualFieldsTarget.classList.add("hidden");
    this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = true; });
  }

  showManualEntry() {
    if (!this.hasManualFieldsTarget) return;

    this.manualFieldsTarget.classList.remove("hidden");
    this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = false; });
    this.manualFieldsTarget.querySelector("input")?.focus();
  }

  memberId() {
    return this.element.querySelector('[name="member_id"]')?.value || "";
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
